/**
 * Entry application component used to compose providers and render Routes.
 */
import React, { StrictMode } from "react";
import type { FC, ReactNode } from "react";
import { useSnackbar } from "notistack";
import type { SnackbarKey } from "notistack";
import {
  AuthProvider,
  GraphQLProvider,
  I18nProvider,
  LocaleProvider,
  MaterialThemeProvider,
  UserProvider,
} from "./providers";
import {
  AutoPageTitle,
  ErrorBoundary,
  NotificationProvider,
  VersionControl,
} from "@/components";
import { AppRoutes } from "./routes";
import CssBaseline from "@mui/material/CssBaseline";
import { IconButton } from "@mui/material";
import { IoMdClose } from "react-icons/io";
import { getPartnerKey } from "@/utils";
import { CookiesProvider } from "react-cookie";
import { BrowserRouter } from "react-router";
import { loadDevMessages, loadErrorMessages } from "@apollo/client/dev";
import { useNetworkStatus } from "@/hooks";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { store } from "./store";
import { Provider } from "react-redux";

const { PUBLIC_URL, VITE_RECAPTCHA_SITE_KEY } = import.meta.env;

const partnerKey = getPartnerKey();

const faviconSizes = ["16x16", "32x32", "64x64"] as const;

const loadFavicons = async (): Promise<void> => {
  // Remove existing favicons
  const existingLinks = document.querySelectorAll("link[rel='icon']");
  existingLinks.forEach((link) => document.head.removeChild(link));

  // Add new favicons if they exist
  for (const size of faviconSizes) {
    const href = `/media/partners/${partnerKey}/favicons/favicon-${size}.png`;

    const link = document.createElement("link");
    link.rel = "icon";
    link.type = "image/png";
    link.sizes = size;
    link.href = href;
    document.head.appendChild(link);
  }
};

loadFavicons().then();

interface RecaptchaWrapperProps {
  children: ReactNode;
}

// Conditional wrapper for GoogleReCaptchaProvider
const RecaptchaWrapper: FC<RecaptchaWrapperProps> = ({ children }) => {
  if (VITE_RECAPTCHA_SITE_KEY) {
    return (
      <GoogleReCaptchaProvider
        reCaptchaKey={VITE_RECAPTCHA_SITE_KEY}
        scriptProps={{
          async: true,
          defer: true,
          appendTo: "head",
        }}
      >
        {children}
      </GoogleReCaptchaProvider>
    );
  }
  return <>{children}</>;
};

const emotionCache = createCache({
  key: "css",
  prepend: true,
});
emotionCache.compat = true;

/**
 * Entry application component used to compose providers and render Routes.
 *
 * This component is the main entry point for the application. It sets up the
 * necessary providers and renders the routes defined in `AppRoutes`.
 */
export default function App() {
  const notificationPosition = {
    vertical: "bottom" as const,
    horizontal: "left" as const,
  };

  if (import.meta.env.MODE !== "production") {
    loadDevMessages();
    loadErrorMessages();
  }

  return (
    <StrictMode>
      <Provider store={store}>
        <CacheProvider value={emotionCache}>
          <CssBaseline />
          <BrowserRouter basename={PUBLIC_URL}>
            <NotificationProvider
              anchorOrigin={notificationPosition}
              autoHideDuration={30000}
              preventDuplicate={true}
              action={(key) => <CloseButton id={key} />}
            >
              <NetworkStatusMonitor />
              <RecaptchaWrapper>
                <CookiesProvider>
                  <AuthProvider>
                    <UserProvider>
                      <LocaleProvider>
                        <GraphQLProvider>
                          <ErrorBoundary>
                            {/* <MetronicLayoutProvider> */}
                            {/*   <MetronicSubheaderProvider> */}
                            {/*     <MetronicSplashScreenProvider> */}
                            {/* <SplashScreenProvider> */}
                            {/* <React.Suspense fallback={<LayoutSplashScreen />}> */}
                            {/* <React.Suspense fallback={<SplashScreen />}> */}
                            <VersionControl>
                              <MaterialThemeProvider>
                                <I18nProvider>
                                  <AutoPageTitle />
                                  {/*<ConfirmationProvider>*/}
                                  {/* <SplashScreen /> */}
                                  <AppRoutes />
                                  {/*</ConfirmationProvider>*/}
                                </I18nProvider>
                              </MaterialThemeProvider>
                            </VersionControl>
                            {/* </React.Suspense> */}
                            {/* </SplashScreenProvider> */}
                            {/* </MetronicSplashScreenProvider> */}
                            {/* </MetronicSubheaderProvider> */}
                            {/* </MetronicLayoutProvider> */}
                          </ErrorBoundary>
                        </GraphQLProvider>
                      </LocaleProvider>
                    </UserProvider>
                  </AuthProvider>
                </CookiesProvider>
              </RecaptchaWrapper>
            </NotificationProvider>
          </BrowserRouter>
        </CacheProvider>
      </Provider>
    </StrictMode>
  );
}

interface CloseButtonProps {
  id: SnackbarKey;
}

const CloseButton: FC<CloseButtonProps> = ({ id }) => {
  const { closeSnackbar } = useSnackbar();

  return (
    <IconButton size="small" onClick={() => closeSnackbar(id)} color="inherit">
      <IoMdClose />
    </IconButton>
  );
};

/**
 * Component to monitor network status and display notifications
 * Must be rendered inside NotificationProvider to access useSnackbar
 */
const NetworkStatusMonitor: FC = () => {
  useNetworkStatus();
  return null;
};
